    import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { formatDateTime } from "../../Utils/newHelper";
import { CustomPagination } from "./CustomPagination";
import { getAllTradesList } from "../../Redux/Action/transaction/allTradesAction";
import { PAGINATION } from "../../Utils/Constants";

const UserTradesModal = ({ isOpen, onClose, user }) => {
  const didMountRef = useRef(false);
  const [userTrades, setUserTrades] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const now = new Date();
  const today = now.toISOString().split("T")[0];

  const startDate = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(formatDate(startDate));
  const [to, setTo] = useState(formatDate(now));
  const [localFrom, setLocalFrom] = useState(formatDate(startDate));
  const [localTo, setLocalTo] = useState(formatDate(now));
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("recent"); // Default filter
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // Debounced search term
  
  const dispatch = useDispatch();

  // Reset state when modal closes or user changes
  useEffect(() => {
    if (!isOpen || !user) {
      // Reset all state values when modal is closed or user changes
      setUserTrades([]);
      setIsDataLoading(true);
      setFrom(formatDate(startDate));
      setTo(formatDate(now));
      setLocalFrom(formatDate(startDate));
      setLocalTo(formatDate(now));
      setTotalPages(0);
      setCurrentPage(1);
      setFilter("recent");
      setSearchTerm("");
      setDebouncedSearchTerm("");
    }
  }, [isOpen, user]);

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Debounce effect for search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to first page when search term actually changes (after debounce)
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleDateApply = () => {
    setFrom(localFrom);
    setTo(localTo);
    setCurrentPage(1); // Reset to first page when date range changes
  };

  const fetchData = () => {
    // Prepare account number string
    let accountNoString = "all";
    
    // Add null check for user
    if (user) {
      if (user.role === 7) {
        // Normal user with single account - convert to string
        accountNoString = String(user.accountno);
      } else if (user.role === 6) {
        // IB user - use ibAccounts if available
        if (user.ibAccounts && user.ibAccounts.length > 0) {
          // Join all account numbers with '&' separator
          accountNoString = user.ibAccounts.map(account => account.accountNo).join("&");
        } else if (user.accountno) {
          // Fallback to main accountno if ibAccounts not available
          accountNoString = String(user.accountno);
        }
      }
    }

    const payload = {
      accountno: accountNoString,
      sdate: `${from} 00:00:00`,
      edate: `${to} 23:59:59`,
      per_page: PAGINATION.PER_PAGE,
      page: currentPage,
      filter: filter, // Add filter to payload
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }) // Add search term to payload if it exists
    };
    
    setIsDataLoading(true);
    dispatch(
      getAllTradesList({
        payload,
        callback: (data) => {
          if (data?.meta?.code === 200) {
            // Updated to match the actual API response structure
            setUserTrades(data?.data?.data || []);
            setTotalPages(data?.data?.pagination?.totalPages || 0);
            setIsDataLoading(false);
          } else {
            setIsDataLoading(false);
          }
        },
      })
    );
  };

  useEffect(() => {
    if (didMountRef.current) {
      const timer = setTimeout(fetchData, 500);
      return () => clearTimeout(timer);
    } else {
      // Add null check for user and isOpen
      if (isOpen && user) {
        fetchData();
        didMountRef.current = true;
      }
    }
  }, [isOpen, user, currentPage, from, to, filter, debouncedSearchTerm]); // Add debouncedSearchTerm to dependency array

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '95%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
              User Trades
            </h2>
            <p style={{ fontSize: '14px', marginTop: '4px', opacity: 0.9 }}>
              {user ? `${user.fname} ${user.lname} (${user.email})` : 'User Trades'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              fontSize: '18px',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
        </div>

        {/* Modal Filters */}
        <div 
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f9fafb'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
              <div>
                <label htmlFor="from-date" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '500' }}>
                  From
                </label>
                <input
                  id="from-date"
                  type="date"
                  value={localFrom}
                  max={today}
                  onChange={(e) => setLocalFrom(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem',
                    width: '150px'
                  }}
                />
              </div>

              <div>
                <label htmlFor="to-date" style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '500' }}>
                  To
                </label>
                <input
                  id="to-date"
                  type="date"
                  value={localTo}
                  min={localFrom}
                  max={today}
                  onChange={(e) => setLocalTo(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem',
                    width: '150px'
                  }}
                />
              </div>

              <button 
                onClick={handleDateApply}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#4338ca'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#4f46e5'}
              >
                Apply
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
              <input
                placeholder="Search by Deal No"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  // Don't reset page here, let the debounce handle the API call
                }}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem',
                  width: '250px'
                }}
              />
              <select 
                value={filter} 
                onChange={(e) => {
                  setFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page when filter changes
                }}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: 'inherit',
                  fontSize: '0.875rem',
                  width: '200px'
                }}
              >
                <option value="recent">Recent Orders</option>
                <option value="lowToHigh">Quantity: Low to High</option>
                <option value="highToLow">Quantity: High to Low</option>
                <option value="maxProfit">Maximum Profit</option>
                <option value="minProfit">Minimum Profit</option>
              </select>
            </div>
          </div>
        </div>

        {/* Modal Content */}
        <div style={{ padding: '24px', overflow: 'auto', flex: 1 }}>
          {isDataLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <div style={{ textAlign: 'center' }}>
                <div className="spinner" style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '4px solid rgba(79, 70, 229, 0.3)', 
                  borderTop: '4px solid #4f46e5', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }}></div>
                <p>Loading trades...</p>
              </div>
            </div>
          ) : (
            <>
              <div style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '0.5rem', 
                overflow: 'hidden',
                maxHeight: '500px',
                overflowY: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Sr.No.</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Account No</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Deal No</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Symbol</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>B/S</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Qty</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Price</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Profit</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userTrades?.length ? (
                      userTrades?.map((item, index) => (
                        <tr 
                          key={item.DealNo || index} 
                          style={{ 
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => e.target.closest('tr').style.backgroundColor = '#f9fafb'}
                          onMouseOut={(e) => e.target.closest('tr').style.backgroundColor = 'transparent'}
                        >
                          <td style={{ padding: '12px 16px', fontSize: '0.875rem' }}>{index + 1}</td>
                          <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>{item?.Accountno || "N/A"}</td>
                          <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>{item?.DealNo || "N/A"}</td>
                          <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>{item?.Symbol || "N/A"}</td>
                          <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>
                            <span 
                              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                backgroundColor: item?.BS === "Buy" ? '#dcfce7' : '#fee2e2',
                color: item?.BS === "Buy" ? '#166534' : '#991b1b'
              }}
            >
              {item?.BS || "N/A"}
            </span>
          </td>
          <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>{item?.Qty || "N/A"}</td>
          <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>{item?.Price || "N/A"}</td>
          <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>
            <span 
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                backgroundColor: 
                  item?.Profit > 0 ? '#dcfce7' : 
                  item?.Profit < 0 ? '#fee2e2' : '#f3f4f6',
                color: 
                  item?.Profit > 0 ? '#166534' : 
                  item?.Profit < 0 ? '#991b1b' : '#6b7280'
              }}
            >
              {item?.Profit !== undefined ? item.Profit : "N/A"}
            </span>
          </td>
          <td style={{ padding: '12px 16px', fontSize: '0.75rem', textAlign: 'center' }}>
            {item?.Time ? formatDateTime(item.Time) : "N/A"}
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan={9} style={{ padding: '40px', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
          No trade orders found for the selected criteria.
        </td>
      </tr>
    )}
  </tbody>
</table>
              </div>
              <div style={{ marginTop: '24px' }}>
                <CustomPagination totalCount={totalPages} current={currentPage} setCurrent={setCurrentPage} />
              </div>
            </>
          )}
        </div>
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UserTradesModal;